import FormContainer from '@components/FormContainer';
import FormSection from '@components/FormSection';
import InputMultiSelect from '@components/InputMultiSelect';
import InputSwitchControlled from '@components/InputSwitch/InputSwitchControlled';
import { Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import { ButtonSaveType } from '@components/ButtonSaveSplit';
import { TalentLanguage } from '../types';
import { useLanguageForm } from '../hooks/useLanguageForm';
import { useLanguageOptions } from '../hooks/useLanguageOptions';
import CertificateForm from './CertificateForm';

interface LanguageFormContainerProps {
    selectedData: TalentLanguage | null;
    setOpenForm: (open: boolean) => void;
    setSelectedData: (data: TalentLanguage | null) => void;
}

const LanguageFormContainer = ({ selectedData, setOpenForm, setSelectedData }: LanguageFormContainerProps) => {
    const {
        form,
        isLoading,
        setSaveType,
        randomKey,
        hasCertificate,
        setHasCertificate,
        onSubmit,
    } = useLanguageForm({ selectedData, setOpenForm, setSelectedData });

    const { languageOptions, levelOptions } = useLanguageOptions(selectedData);
    const { control, handleSubmit, setValue, watch, clearErrors, setError, reset } = form;

    return (
        <FormContainer
            onBack={() => setOpenForm(false)}
            loading={isLoading}
            formState="edit"
            onButtonSplitChange={(type: ButtonSaveType) => setSaveType(type)}
            buttonSplitText={{
                [ButtonSaveType.SAVE_AND_RETURN]: 'Enregistrer',
                [ButtonSaveType.SAVE_AND_ADD]: 'Ajouter une nouvelle langue',
            }}
            formTitle={selectedData ? 'Modifier une langue' : 'Ajouter une langue'}
            onCancel={() => {
                reset();
                setOpenForm(false);
                setSelectedData(null);
            }}
            breadcrumbs={[]}
            onSubmit={handleSubmit(onSubmit)}
            submitText={!isEmpty(selectedData) ? 'Enregistrer' : 'Suivant'}>
            <FormSection title="Informations linguistiques">
                <Stack>
                    <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2.5, zIndex: 2 }}>
                        <Stack sx={{ flex: 1 }}>
                            <InputMultiSelect
                                viewOnly={false}
                                single
                                control={control}
                                name="language"
                                label="Langue"
                                placeholder="Sélectionnez une langue"
                                options={languageOptions}
                                defaultValue={selectedData?.languages?.[0]?.language?.[0]?.name}
                                required
                            />
                        </Stack>
                        <Stack sx={{ flex: 1 }}>
                            <InputMultiSelect
                                viewOnly={false}
                                single
                                control={control}
                                name="level"
                                label="Niveau"
                                placeholder="Niveau de maîtrise de la langue"
                                options={levelOptions}
                                defaultValue={selectedData?.languages?.[0]?.level?.[0]?.level}
                                required
                            />
                        </Stack>
                    </Stack>
                    <Stack sx={{ mb: 1.875, mt: 1.25 }}>
                        <InputSwitchControlled
                            control={control}
                            name="addCertificate"
                            defaultValue={hasCertificate}
                            onChange={(value: boolean) => setHasCertificate(value)}
                            title="J'ai un diplôme/certificat"
                            containerStyle={{ marginBottom: 2 }}
                        />
                    </Stack>
                    {hasCertificate && (
                        <CertificateForm
                            control={control}
                            clearErrors={clearErrors}
                            watch={watch}
                            setValue={setValue}
                            setError={setError}
                            randomKey={randomKey}
                            selectedData={selectedData}
                        />
                    )}
                </Stack>
            </FormSection>
        </FormContainer>
    );
};

export default LanguageFormContainer;
